import { Button1, Button2 } from "../buttons/Buttons";
import ProgressBar from "../progress-bar/ProgressBar";
import Select from "../select/Select";
import SmallCircle from "../small-circle/SmallCircle";
import { SuccessTag } from "../tag/Tag";

import infoIcon from "../../assets/icons/inforound-icon.png";
import winterIcon from "../../assets/icons/winter-Icon.png";
import tagIcon from "../../assets/icons/tag-icon.png";
import { CiCalendar } from "react-icons/ci";
import plusIcon from "../../assets/icons/plus-circle-white.png";
import { DatePicker, Input, Modal, Spin } from "antd";
import ManageMySpending from "./ManageMySpending";
import PieChart from "../piechart/Piechart";
import billIcon from "../../assets/icons/bill-Icon.png";
import rentIcon from "../../assets/images/building.png";
import cartIcon from "../../assets/images/shopping-cart.png";
import noteIcon from "../../assets/images/bank-note.png";
import creditcardIcon from "../../assets/images/creditcard-normal.png";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Meter from "../meter/Meter";
import { useBudgets, useTransactions, useBills } from "../../data/live";

const CATEGORY_ICON = { Rent: rentIcon, Groceries: cartIcon, "Mortgage & Loans": noteIcon, "Others & ATM": creditcardIcon };
const CATEGORY_COLOR = { Rent: "#E8550D", "Mortgage & Loans": "#F2E103", Groceries: "#1BBB6B", "Others & ATM": "#2660CE" };
const fallbackColor = (c) => { let h=0; for(let i=0;i<(c||"").length;i++) h=(h*31+c.charCodeAt(i))|0; return ["#E8550D","#F2E103","#1BBB6B","#2660CE","#922590","#3CADF1"][Math.abs(h)%6]; };
const fmt = (n, ccy="AUD") => `${Number(n||0).toLocaleString(undefined,{minimumFractionDigits:0,maximumFractionDigits:0})} ${ccy}`;

const BudgetReport = () => {
  const [openModal, setOpenModal] = useState(false);
  const { items: budgets, loading: bLoading } = useBudgets();
  const { items: transactions, loading: tLoading } = useTransactions();
  const { bills, loading: blLoading } = useBills();
  const navigate = useNavigate();
  const [dateFilter, setDateFilter] = useState(null);
  const [spendingCurrency, setSpendingCurrency] = useState("ALL"); // "ALL" = all currencies

  // Australian Financial Year runs 1 July to 30 June. Build last 3 FYs ending at current.
  const currentFY = useMemo(() => {
    const now = new Date();
    const startYear = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
    return `${startYear}-${startYear + 1}`;
  }, []);
  const taxYearOptions = useMemo(() => {
    const now = new Date();
    const startYear = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
    return [-2, -1, 0].map((offset) => {
      const y = startYear + offset;
      return { name: `${y}-${y + 1}`, value: `${y}-${y + 1}` };
    });
  }, []);
  const [taxYear, setTaxYear] = useState(currentFY);

  const loading = bLoading && tLoading && blLoading;
  /** Filter transactions by selected date if any */
  const filteredTx = useMemo(() => {
    if (!dateFilter) return transactions;
    const cutoff = dateFilter.toDate ? dateFilter.toDate() : new Date(dateFilter);
    const startOfMonth = new Date(cutoff.getFullYear(), cutoff.getMonth(), 1);
    const endOfMonth = new Date(cutoff.getFullYear(), cutoff.getMonth() + 1, 0, 23, 59, 59);
    return transactions.filter(t => {
      const d = new Date(t.raw?.createdAt || t.date);
      return d >= startOfMonth && d <= endOfMonth;
    });
  }, [transactions, dateFilter]);

  /** Total Spending pie/list currency filter */
  const spendingTx = useMemo(() => {
    if (!spendingCurrency || spendingCurrency === "ALL") return filteredTx;
    return filteredTx.filter((t) => t.currency === spendingCurrency);
  }, [filteredTx, spendingCurrency]);

  /** Tax-deduction window, Australian FY (1 Jul to 30 Jun) for selected year. */
  const taxYearTotals = useMemo(() => {
    if (!taxYear) return { deduction: 0, currency: "AUD" };
    const [s] = taxYear.split("-").map(Number);
    const start = new Date(s, 6, 1); // 1 July s
    const end = new Date(s + 1, 5, 30, 23, 59, 59); // 30 June s+1
    const inWindow = transactions.filter((t) => {
      const d = new Date(t.raw?.createdAt || t.date);
      return d >= start && d <= end && t.direction === "out";
    });
    // We don't yet flag transactions as "deductible"; show total spend as a
    // deductible-eligible upper bound until that flag is wired.
    const total = inWindow.reduce((s, t) => s + Number(t.amount || 0), 0);
    const ccy = inWindow[0]?.currency || "AUD";
    return { deduction: total, currency: ccy };
  }, [transactions, taxYear]);

  /** Income vs Expense from real transactions */
  const totalReceived = useMemo(() => filteredTx.filter(t=>t.direction==="in").reduce((s,t)=>s+Number(t.amount||0),0), [filteredTx]);
  const totalSpent = useMemo(() => filteredTx.filter(t=>t.direction==="out").reduce((s,t)=>s+Number(t.amount||0),0), [filteredTx]);

  /** Budget totals */
  const incomeBudget = useMemo(() => budgets.filter(b=>b.category.toLowerCase().includes("income")).reduce((s,b)=>s+Number(b.limit||0),0), [budgets]);
  const expenseBudget = useMemo(() => budgets.filter(b=>!b.category.toLowerCase().includes("income")).reduce((s,b)=>s+Number(b.limit||0),0), [budgets]);

  const incomePercent = incomeBudget > 0 ? Math.min(100, Math.round((totalReceived / incomeBudget) * 100)) : (totalReceived > 0 ? 100 : 0);
  const expensePercent = expenseBudget > 0 ? Math.min(100, Math.round((totalSpent / expenseBudget) * 100)) : (totalSpent > 0 ? 100 : 0);

  const incomeDetails = [
    { name: "Received", amount: `$${fmt(totalReceived)}` },
    { name: "Budget", amount: `$${fmt(incomeBudget)}` },
    { name: "Remaining", amount: `$${fmt(Math.max(0, incomeBudget - totalReceived))}` },
  ];
  const expenseDetails = [
    { name: "Spent", amount: `$${fmt(totalSpent)}` },
    { name: "Budget", amount: `$${fmt(expenseBudget)}` },
    { name: "Remaining", amount: `$${fmt(Math.max(0, expenseBudget - totalSpent))}` },
  ];

  /** Top spending categories for pie chart (currency-filtered) */
  const categoryTotals = useMemo(() => {
    const map = {};
    for (const t of spendingTx) {
      if (t.direction !== "out") continue;
      const cat = t.category || "Others & ATM";
      map[cat] = (map[cat] || 0) + Number(t.amount || 0);
    }
    return Object.entries(map).sort(([,a],[,b])=>b-a).slice(0,4);
  }, [spendingTx]);

  const spendingTotal = useMemo(
    () => spendingTx.filter((t) => t.direction === "out").reduce((s, t) => s + Number(t.amount || 0), 0),
    [spendingTx]
  );

  const totalSpendingPieChartData = useMemo(() => {
    const items = categoryTotals.map(([cat, amt]) => ({
      name: cat,
      color: CATEGORY_COLOR[cat] || fallbackColor(cat),
      icon: CATEGORY_ICON[cat] || creditcardIcon,
      amount: `-${fmt(amt, spendingCurrency === "ALL" ? "AUD" : spendingCurrency)}`,
      percent: spendingTotal > 0 ? Math.round((amt / spendingTotal) * 100) : 0,
    }));
    const remaining = spendingTotal > 0 ? Math.max(0, 100 - items.reduce((s, i) => s + i.percent, 0)) : 100;
    if (remaining > 0) items.push({ name: "empty", color: "#E3E3E3", percent: remaining });
    return items;
  }, [categoryTotals, spendingTotal, spendingCurrency]);

  const currencyOptions = useMemo(() => {
    const set = new Set(transactions.map((t) => t.currency).filter(Boolean));
    return [{ name: "All currencies", value: "ALL" }, ...Array.from(set).map((c) => ({ name: c, value: c }))];
  }, [transactions]);

  /** Bills summary for pie chart */
  const billsSummary = useMemo(() => {
    const paid = (bills||[]).filter(b=>b.status==="PAID");
    const overdue = (bills||[]).filter(b=>b.status==="OVERDUE");
    const dueSoon = (bills||[]).filter(b=>b.status!=="PAID"&&b.status!=="OVERDUE");
    const paidAmt = paid.reduce((s,b)=>s+Number(b.amount||0),0);
    const overdueAmt = overdue.reduce((s,b)=>s+Number(b.amount||0),0);
    const dueSoonAmt = dueSoon.reduce((s,b)=>s+Number(b.amount||0),0);
    const total = paidAmt + overdueAmt + dueSoonAmt;
    return [
      { name: "Due Soon", percent: total>0?Math.round((dueSoonAmt/total)*100):0, amount: `-${fmt(dueSoonAmt)}`, color: "#F7A64F" },
      { name: "Paid", percent: total>0?Math.round((paidAmt/total)*100):0, amount: `-${fmt(paidAmt)}`, color: "#1BBB6B" },
      { name: "Overdue", percent: total>0?Math.round((overdueAmt/total)*100):0, amount: `-${fmt(overdueAmt)}`, color: "#FC555B" },
    ];
  }, [bills]);

  /** Budget report details cards */
  const budgetReportDetails = useMemo(() => {
    const watched = (bills||[]).filter(b=>b.status!=="PAID").length;
    const critical = (bills||[]).filter(b=>b.status==="OVERDUE").length;
    const toSetup = Math.max(0, budgets.length > 0 ? 0 : 2);
    return [
      { icon: billIcon, name: "Bills being Watched", info: String(watched) },
      { icon: billIcon, name: "Bills to setup", info: String(toSetup) },
      { icon: billIcon, name: "Critical bills to pay", info: String(critical) },
    ];
  }, [bills, budgets]);

  /** Emergency fund = savings budget if exists */
  const savingsBudget = useMemo(() => budgets.find(b=>b.category.toLowerCase().includes("saving")), [budgets]);
  const savingsTarget = Number(savingsBudget?.limit || 0);
  const savingsSaved = Number(savingsBudget?.spent || 0);
  const savingsRemaining = Math.max(0, savingsTarget - savingsSaved);
  const savingsPercent = savingsTarget > 0 ? Math.min(100, Math.round((savingsSaved/savingsTarget)*100)) : 0;

  const emergencyFundDetails = [
    { name: "Saved", amount: `$${fmt(savingsSaved)}` },
    { name: "Target", amount: `$${fmt(savingsTarget)}` },
    { name: "Remaining", amount: `$${fmt(savingsRemaining)}` },
  ];

  if (loading) {
    return (
      <ManageMySpending
        navName={"Income & Expenses Dashboard"}
        showQuickAction={true}
        pageClassName="dashboard-page report-theme-page budget-report-page"
        contentClassName="dashboard-content"
      >
        <div className="d-flex justify-content-center py-5"><Spin size="large" /></div>
      </ManageMySpending>
    );
  }

  return (
    <>
      {/* Modal For Setup Now */}
      <Modal
        title={<p className="text-center fs-5 fw-semibold">Setup</p>}
        centered width={350} open={openModal}
        onCancel={() => setOpenModal(false)} onOk={() => setOpenModal(false)}
        okText={"Save"}
        okButtonProps={{ className: "bg-black text-white rounded-pill" }}
        cancelButtonProps={{ className: "rounded-pill", style: { borderColor: "#E7040C ", color: "#E7040C" } }}
      >
        <div className="d-flex flex-column  gap-3">
          <span className="fs12px" style={{ color: "#7D7D7D" }}>
            By providing these details you'll be able to see how your spending
            and income comapre to people like you.
          </span>
          <div className="d-flex flex-column gap-2">
            <label className="fs12px">Income</label>
            <Input className="p-3 " placeholder="Your Income" />
          </div>
          <div className="d-flex flex-column gap-2">
            <label className="fs12px">Date of Birth</label>
            <DatePicker className="p-3" placeholder="DD-MM-YY" />
          </div>
          <div className="d-flex flex-column gap-2">
            <label className="fs12px">Location</label>
            <Input className="p-3 " placeholder="Your Location" />
          </div>
          <div className="d-flex flex-column gap-2">
            <label className="fs12px">Occupation</label>
            <Input className="p-3 " placeholder="Your occupation" />
          </div>
        </div>
      </Modal>

      <ManageMySpending
        navName={"Income & Expenses Dashboard"}
        showQuickAction={true}
        pageClassName="dashboard-page report-theme-page budget-report-page"
        contentClassName="dashboard-content"
      >
        <div className="report-shell">
          <div className="report-overview">
            <div>
              <span className="dashboard-eyebrow">Income and expenses</span>
              <h2>Budget Report</h2>
              <p>Track budgets, cashflow, bills, tax deductions, goals, and risk indicators in one dashboard workspace.</p>
            </div>
          </div>

          <div className="d-flex flex-column gap-3 mt-4">
          <div className="report-toolbar d-flex align-items-center justify-content-md-between flex-wrap gap-3 justify-content-center">
            <span className="fs-5 fw-medium">Budget Report</span>
            <Button2
              buttonName={"Add an item to the dashboard"}
              icon={<img src={plusIcon} alt="add" />}
              className={"btn-dark"} divClassName={"gap-2"}
            />
          </div>

          <div className="d-flex justify-content-between gap-3 align-items-center w-100 overflow-md-visible overflow-auto">
            {budgetReportDetails.map(({ icon, name, info }, key) => (
              <div key={key} className="budget-metric-card d-flex align-items-center gap-3 p-3"
                style={{ borderColor: "#E3E3E3", minWidth: "22.5rem", height: "6.5rem" }}>
                <img src={icon} alt="bill" />
                <div className="d-flex flex-column gap-2">
                  <span className="fs14px fw-medium" style={{ color: "#7D7D7D" }}>{name}</span>
                  <span className="fs-5 fw-medium">{info}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="d-flex flex-column gap-5 mt-5">
          <div className="row gap-lg-0 gap-5">
            <div className="col-lg-6">
              <div className="d-flex flex-column gap-5">
                {/* My Budget  */}
                <div className="d-flex flex-column gap-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-medium fs-5">My Budget</span>
                    <DatePicker placeholder="Custom date" className="rounded-pill" onChange={(d) => setDateFilter(d)} />
                  </div>

                  <div className="report-card d-flex flex-column gap-4 p-3">
                    {/* Income */}
                    <div className="d-flex flex-column gap-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <label className="fs-6 fw-semibold">Income</label>
                        <span className="fs-6 fw-semibold cursor-pointer" style={{ color: "#1bbb6b" }} onClick={() => navigate("/spending-report")}>View Details</span>
                      </div>
                      <ProgressBar percent={incomePercent} />
                      <div className="d-flex justify-content-between align-items-center" style={{ width: "90%" }}>
                        {incomeDetails.map(({ name, amount }, key) => (
                          <span key={key} className="fs12px ">
                            {name}: {"  "}
                            <span className="fw-semibold" style={{ color: "#1bbb6b" }}>{amount}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                    {/* Expense */}
                    <div className="d-flex flex-column gap-2">
                      <ProgressBar label={"Expense"} percent={expensePercent}
                        labelClassName="fs-6 fw-medium" strokeColor="#FC555B" width="80%" />
                      <div className="d-flex justify-content-between align-items-center " style={{ width: "90%" }}>
                        {expenseDetails.map(({ name, amount }, key) => (
                          <span key={key} className="fs12px ">
                            {name}: {"  "}
                            <span className="fw-semibold" style={{ color: "#FC555B" }}>{amount}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Total Spending */}
                <div className="d-flex flex-column gap-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-medium fs-5">Total Spending</span>
                    <div className="d-flex gap-2">
                      <Select
                        className="rounded-pill w-50"
                        value={spendingCurrency}
                        defaultValue="All currencies"
                        options={currencyOptions}
                        onChange={(e) => setSpendingCurrency(e.target.value)}
                      />
                      <DatePicker placeholder="Custom date" className="rounded-pill" onChange={(d) => setDateFilter(d)} />
                    </div>
                  </div>

                  <div className="report-card d-flex justify-content-md-between flex-wrap flex-md-nowrap justify-content-center gap-4 align-items-center p-3">
                    <div style={{ width: "35%" }}>
                      <PieChart textOffset={-34} pieData={totalSpendingPieChartData} />
                    </div>
                    <div className="d-flex flex-column gap-2 w-75">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fs12px">Top categories</span>
                        <span className="fs14px fw-semibold cursor-pointer" style={{ color: "#1bbb6b" }} onClick={() => navigate("/spending-report")}>View Details</span>
                      </div>
                      {totalSpendingPieChartData.map(({ name, icon, percent, color, amount }, key) => (
                        !(name === "empty") && (
                          <div key={key} className="d-flex justify-content-between align-items-center">
                            <div className="d-flex gap-2 align-items-center">
                              <SmallCircle color={color} />
                              <img src={icon} alt="" />
                              <span className="fw-medium fs14px">{name}</span>
                            </div>
                            <div className="d-flex gap-2 align-items-center">
                              <span className="fs12px fw-medium" style={{ color: "#FC555B" }}>{amount}</span>
                              <span className="fs12px" style={{ color: "#7D7D7D" }}>{percent}%</span>
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              {/* Cash Flow */}
              <div className="d-flex flex-column gap-2">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-medium fs-5">Cashflow</span>
                  <DatePicker placeholder="Custom date" className="rounded-pill" onChange={(d) => setDateFilter(d)} />
                </div>
                <div className="report-card d-flex flex-column gap-4 p-3">
                  <div className="d-flex justify-content-between">
                    <div className="d-flex flex-column">
                      <span className="fs12px" style={{color:"#7D7D7D"}}>Total In</span>
                      <span className="fw-semibold" style={{color:"#1bbb6b"}}>{fmt(totalReceived)}</span>
                    </div>
                    <div className="d-flex flex-column">
                      <span className="fs12px" style={{color:"#7D7D7D"}}>Total Out</span>
                      <span className="fw-semibold" style={{color:"#FC555B"}}>{fmt(totalSpent)}</span>
                    </div>
                    <div className="d-flex flex-column">
                      <span className="fs12px" style={{color:"#7D7D7D"}}>Net</span>
                      <span className="fw-semibold">{fmt(totalReceived - totalSpent)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row gap-lg-0 gap-5">
            <div className="col-lg-6">
              {/*My Bills  */}
              <div className="d-flex flex-column gap-2">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-medium fs-5">My Bills</span>
                  <DatePicker placeholder="Custom date" className="rounded-pill" onChange={(d) => setDateFilter(d)} />
                </div>

                <div className="d-flex justify-content-between gap-5 align-items-center border rounded-3 p-3">
                  <div style={{ width: "35%" }}>
                    <PieChart textOffset={-30} pieData={billsSummary} />
                  </div>
                  <div className="d-flex flex-column gap-2 w-75">
                    <span className="fs14px fw-semibold cursor-pointer text-end" style={{ color: "#1bbb6b" }} onClick={() => navigate("/my-bills")}>View Details</span>
                    {billsSummary.map(({ name, percent, color, amount }, key) => (
                      <div key={key} className="d-flex justify-content-between align-items-center">
                        <div className="d-flex gap-2 align-items-center">
                          <SmallCircle color={color} />
                          <div className="d-flex flex-column gap-1">
                            <span className="fw-medium fs14px">{name}</span>
                            <span className="fs12px" style={{ color: "#7D7D7D" }}>{percent}%</span>
                          </div>
                        </div>
                        <span className="fs12px fw-medium" style={{ color: "#FC555B" }}>{amount}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              {/* Tax Deductions */}
              <div className="report-card d-flex flex-column gap-4 p-3 px-4">
                <div className="d-flex justify-content-between align-items-center flex-md-nowrap flex-wrap gap-3">
                  <span className="fs-5 fw-semibold">Tax deductions</span>
                  <div className="d-flex gap-2 align-items-center">
                    <Button2 buttonName={"Income Tax"} className={"border"} icon={<img src={tagIcon} alt="tag" />} divClassName={"gap-2"} />
                    <span className="fw-semibold" style={{ color: "#1bbb6b" }}>View details</span>
                  </div>
                </div>
                <div className="d-flex justify-content-between align-items-center flex-md-nowrap flex-wrap gap-3">
                  <div className="d-flex flex-column gap-1">
                    <span className="fs12px" style={{ color: "#7D7D7D" }}>Total Deductions for FY {taxYear}</span>
                    <span className="fs-5 fw-semibold">{fmt(taxYearTotals.deduction, taxYearTotals.currency)}</span>
                  </div>
                  {taxYearTotals.deduction === 0 && (
                    <SuccessTag tagName={"You dont have any transaction to review"} className={"rounded-2"} />
                  )}
                </div>
                <hr className="opacity-25" style={{ color: "#7D7D7D" }} />
                <div className="d-flex justify-content-between align-items-center flex-md-nowrap flex-wrap gap-3">
                  <div className="d-flex flex-column gap-2">
                    <Select
                      className="rounded-pill"
                      value={taxYear}
                      defaultValue="Select financial year"
                      options={taxYearOptions}
                      onChange={(e) => setTaxYear(e.target.value)}
                    />
                    <span className="fs12px" style={{ color: "#7D7D7D" }}>{taxYearTotals.currency} in transaction to review</span>
                  </div>
                  <div className="d-flex gap-3 align-items-center">
                    <span className="fw-semibold" style={{ color: "#1bbb6b" }}>{fmt(taxYearTotals.deduction, taxYearTotals.currency)} Tax deduction</span>
                    <span className="fw-semibold cursor-pointer" style={{ color: "#1bbb6b" }}>View year</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row gap-lg-0 gap-5">
            <div className="col-lg-6">
              {/* People Like Me */}
              <div className="report-card d-flex flex-column gap-4 p-3 px-4">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fs-5 fw-semibold ">People Like Me</span>
                  <span className=" fw-semibold cursor-pointer" style={{ color: "#1bbb6b" }} onClick={() => setOpenModal(true)}>View details</span>
                </div>
                <div className="d-flex flex-column gap-3 align-items-center position-relative">
                  <div className="text-center py-4" style={{color:"#B0B0B0"}}>
                    <p className="fs14px">Setup your profile to compare spending with people like you.</p>
                  </div>
                  <Button1 buttonName={"Setup Now"} onClick={() => setOpenModal(true)}
                    className={"btn-dark px-4"} />
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              {/* Risk Profile */}
              <div className="report-card d-flex flex-column gap-4 p-3 px-4">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fs-5 fw-semibold">Risk Profile</span>
                  <span className="fw-semibold cursor-pointer" style={{ color: "#1bbb6b" }} onClick={() => navigate("/budget-tracking")}>Retake Assessment</span>
                </div>
                <div className="d-flex justify-content-md-between justify-content-center gap-2 align-items-center flex-md-nowrap flex-wrap gap-2 ">
                  <div className="d-flex flex-column">
                    <Meter />
                    <span className="fs-3 fw-bold align-self-center" style={{ color: "#0E2C24" }}>685</span>
                  </div>
                  <div className="d-flex flex-column gap-1 rounded-2 p-3" style={{ color: "#F8B063", backgroundColor: "#FEF5EB" }}>
                    <span className="fw-semibold">Medium</span>
                    <span className="fs13px fw-medium">
                      This means that you will be less comfortable, but not
                      overly concerned by short terms variations in investment
                      returns, including negative returns when they occur
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row gap-lg-0 gap-5">
            <div className="col-lg-6">
              {/* Emergency Fund */}
              <div className="report-card d-flex flex-column gap-4 p-3 px-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex gap-2 align-items-center">
                    <img src={infoIcon} alt="info" />
                    <span className="fs-5 fw-semibold">Emergency Fund</span>
                  </div>
                  <span className="fw-semibold cursor-pointer" style={{ color: "#1bbb6b" }} onClick={() => navigate("/budget-setup")}>View tracking</span>
                </div>
                <div className="d-flex flex-column gap-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <label className="fs-6 fw-semibold">Emergency Fund</label>
                    <div className="d-flex gap-1 align-items-center">
                      <CiCalendar color="#7D7D7D" />
                      <span className="fs13px" style={{ color: "#7D7D7D" }}>{new Date().toLocaleDateString("en-AU",{day:"numeric",month:"long",year:"numeric"})}</span>
                    </div>
                  </div>
                  <ProgressBar percent={savingsPercent} />
                  <div className="d-flex justify-content-between align-items-center" style={{ width: "90%" }}>
                    {emergencyFundDetails.map(({ name, amount }, key) => (
                      <span key={key} className="fs12px ">
                        {name}: {"  "}
                        <span className="fw-semibold" style={{ color: "#1bbb6b" }}>{amount}</span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              {/* Winter Vacation */}
              <div className="report-card d-flex flex-column gap-4 p-3 px-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex gap-2 align-items-center">
                    <img src={winterIcon} alt="info" />
                    <span className="fs-5 fw-semibold">Winter Vacation</span>
                  </div>
                  <span className="fw-semibold cursor-pointer" style={{ color: "#1bbb6b" }} onClick={() => navigate("/budget-setup")}>View tracking</span>
                </div>
                <div className="d-flex flex-column gap-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <label className="fs-6 fw-semibold">Winter Vacation</label>
                    <div className="d-flex gap-1 align-items-center">
                      <CiCalendar color="#7D7D7D" />
                      <span className="fs13px" style={{ color: "#7D7D7D" }}>{new Date().toLocaleDateString("en-AU",{day:"numeric",month:"long",year:"numeric"})}</span>
                    </div>
                  </div>
                  <ProgressBar percent={savingsPercent} />
                  <div className="d-flex justify-content-between align-items-center" style={{ width: "90%" }}>
                    {emergencyFundDetails.map(({ name, amount }, key) => (
                      <span key={key} className="fs12px ">
                        {name}: {"  "}
                        <span className="fw-semibold" style={{ color: "#1bbb6b" }}>{amount}</span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </ManageMySpending>
    </>
  );
};

export default BudgetReport;
